"use client"

import { useEffect, useState } from "react"
import { useToast } from "@/components/ui/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  ToastProgress,
} from "@/components/ui/toast"

// Derivar o tipo de toast do hook useToast
type ToastItem = ReturnType<typeof import("@/components/ui/use-toast").useToast>["toasts"][number]

// Componente para gerenciar um único toast com seu progresso
function ToastWithProgress({ toast }: { toast: ToastItem }) {
  const { id, title, description, action, variant, ...props } = toast
  // Converter variant null para undefined para satisfazer o tipo de Toast
  const safeVariant: "default" | "destructive" | undefined = variant == null ? undefined : variant
  const [progress, setProgress] = useState(100)
  
  useEffect(() => {
    const startTime = Date.now()
    const duration = 20000 // 20 segundos, deve ser igual ao TOAST_REMOVE_DELAY
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100)
      setProgress(remaining)
      
      if (remaining <= 0) {
        clearInterval(interval)
      }
    }, 100)
    
    return () => clearInterval(interval)
  }, [id])
  
  return (
    <Toast key={id} {...props} variant={safeVariant}>
      <ToastProgress value={progress} variant={safeVariant} />
      <div className="grid gap-1">
        {title && <ToastTitle data-test="toast-title">{title}</ToastTitle>}
        {description && (
          <ToastDescription data-test="toast-description">{description}</ToastDescription>
        )}
      </div>
      {action}
      <ToastClose />
    </Toast>
  )
}

export function Toaster() {
  const { toasts } = useToast()

  return (
    <ToastProvider>
      {toasts.map((toast) => (
        <ToastWithProgress key={toast.id} toast={toast} />
      ))}
      <ToastViewport />
    </ToastProvider>
  )
}
